import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Rating,
  Avatar,
  CircularProgress,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Translate as TranslateIcon,
  Article as ArticleIcon,
  AdsClick as CtaIcon
} from '@mui/icons-material';
import { homePageService, globalService } from '../../services/firebase';
import ImageUploadField from '../../components/Common/ImageUploadField';
import { useTranslation } from 'react-i18next';
import { getTranslatedData, setTranslatedData } from '../../utils/getTranslatedData';

const languages = ['tr', 'en', 'ar'];
const languageLabels: { [key: string]: string } = {
  tr: 'Türkçe',
  en: 'English',
  ar: 'العربية',
};

interface LanguageTabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function LanguageTabPanel(props: LanguageTabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`language-tabpanel-${index}`}
      aria-labelledby={`language-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`homepage-tabpanel-${index}`}
      aria-labelledby={`homepage-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ManageHomePagePage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [homePageData, setHomePageData] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'slide' | 'feature' | 'stat' | 'testimonial'>('slide');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [currentLang, setCurrentLang] = useState('tr');
  const [translatingSection, setTranslatingSection] = useState<string | null>(null);

  const defaultData = {
    heroSection: { isActive: true, autoSlide: true, slideInterval: 5000, slides: [] },
    featuresSection: { isActive: true, features: [] },
    aboutUsSection: { isActive: true, title: {}, subtitle: {}, description: {}, image: '', ctaText: {}, ctaLink: '' },
    popularToursSection: { isActive: true, title: {}, subtitle: {} },
    statsSection: { isActive: true, stats: [] },
    testimonialsSection: { isActive: true, title: {}, subtitle: {}, testimonials: [] },
    blogSection: { isActive: true, title: {}, subtitle: {} },
    ctaSection: { isActive: true, title: {}, subtitle: {}, primaryCtaText: {}, primaryCtaLink: '', secondaryCtaText: {}, secondaryCtaLink: '' },
  };

  useEffect(() => {
    fetchHomePageData();
  }, []);

  const fetchHomePageData = async () => {
    try {
      setLoading(true);
      setError(null);
      let data = await homePageService.getHomePageData();
      
      if (!data) {
        await homePageService.createDefaultHomePageData();
        data = await homePageService.getHomePageData();
      }
      
      if (!data) {
        setError("Varsayılan ana sayfa verileri oluşturulamadı veya yüklenemedi.");
        setLoading(false);
        return;
      }
      
      const transformedData = {
        ...defaultData,
        ...data,
        // Eski veri yapısı uyumluluğu için
        heroSection: data.heroSection || { ...defaultData.heroSection, slides: data.heroSlides || [] },
        featuresSection: data.featuresSection || { ...defaultData.featuresSection, features: data.features || [] },
        aboutUsSection: data.aboutUsSection || data.aboutSection || defaultData.aboutUsSection,
        popularToursSection: data.popularToursSection || data.featuredToursSection || defaultData.popularToursSection,
        statsSection: data.statsSection || { ...defaultData.statsSection, stats: data.stats || [] },
        testimonialsSection: data.testimonialsSection || { ...defaultData.testimonialsSection, testimonials: data.testimonials || [] },
      };
      
      setHomePageData(transformedData);
    } catch (err) {
      console.error('Ana sayfa verileri yüklenirken hata:', err);
      setError('Ana sayfa verileri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!homePageData) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await homePageService.updateHomePageData(homePageData);
      setSuccess('Ana sayfa verileri başarıyla kaydedildi');
    } catch (err) {
      console.error('Ana sayfa verileri kaydedilirken hata:', err);
      setError('Ana sayfa verileri kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);
  const handleLangChange = (_event: React.SyntheticEvent, newValue: string) => setCurrentLang(newValue);

  const updateSection = (path: string, value: any) => {
    if (!homePageData) return;
    const keys = path.split('.');
    const newData = JSON.parse(JSON.stringify(homePageData));
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]] = current[keys[i]] || {};
    }
    current[keys[keys.length - 1]] = value;
    setHomePageData(newData);
  };

  const handleTranslateSection = async (sectionKey: string, fields: string[]) => {
    if (!homePageData) return;
    setTranslatingSection(sectionKey);
    try {
      let updatedSection = { ...homePageData[sectionKey] };
      for (const field of fields) {
        const sourceText = getTranslatedData(updatedSection[field], 'tr');
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en', 'ar'] });
          updatedSection = setTranslatedData(updatedSection, field, 'en', translations.en);
          updatedSection = setTranslatedData(updatedSection, field, 'ar', translations.ar);
        }
      }
      setHomePageData({ ...homePageData, [sectionKey]: updatedSection });
      setSuccess(`${sectionKey} bölümü başarıyla çevrildi.`);
    } catch (err) {
      setError('Çeviri sırasında bir hata oluştu.');
    } finally {
      setTranslatingSection(null);
    }
  };
  
  const openDialog = (type: 'slide' | 'feature' | 'stat' | 'testimonial', item?: any) => {
    setDialogType(type);
    const emptyLangObj = { tr: '', en: '', ar: '' };
    const baseItem = { id: item?.id || Date.now().toString(), isActive: item?.isActive ?? true };
  
    const itemData: { [key: string]: any } = {
      slide: { title: {}, subtitle: {}, description: {}, ctaText: {}, ctaLink: '', backgroundImage: '' },
      feature: { title: {}, description: {}, icon: '' },
      stat: { title: {}, value: '', icon: '' },
      testimonial: { author: {}, location: {}, quote: {}, avatar: '', rating: 5 },
    };
  
    let editingData = { ...itemData[type] };
    Object.keys(editingData).forEach(key => {
      const isTranslatable = typeof editingData[key] === 'object';
      const itemValue = item?.[key];
      if (isTranslatable) {
        if (typeof itemValue === 'string') editingData[key] = { ...emptyLangObj, tr: itemValue };
        else if (itemValue) editingData[key] = { ...emptyLangObj, ...itemValue };
        else editingData[key] = { ...emptyLangObj };
      } else {
        editingData[key] = itemValue ?? editingData[key];
      }
    });
  
    setEditingItem({ ...baseItem, ...editingData });
    setDialogOpen(true);
  };
  

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setCurrentLang('tr');
  };

  const handleDialogChange = (field: string, value: any, lang?: string) => {
    if (!editingItem) return;
    setEditingItem((prev: any) => {
      if (lang) {
        return { ...prev, [field]: { ...(prev[field] || {}), [lang]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleTranslateDialog = async () => {
    if (!editingItem) return;
    setTranslatingSection('dialog');
    try {
      let updatedItem = { ...editingItem };
      const fields: { [key: string]: string[] } = {
        slide: ['title', 'subtitle', 'description', 'ctaText'],
        feature: ['title', 'description'],
        stat: ['title'],
        testimonial: ['author', 'location', 'quote'],
      };
      const fieldsToTranslate = fields[dialogType] || [];
      for (const field of fieldsToTranslate) {
        const sourceText = getTranslatedData(updatedItem[field], 'tr');
        if (sourceText) {
          const translations = await globalService.translateTexts({ text: sourceText, sourceLang: 'tr', targetLangs: ['en', 'ar'] });
          updatedItem = setTranslatedData(updatedItem, field, 'en', translations.en);
          updatedItem = setTranslatedData(updatedItem, field, 'ar', translations.ar);
        }
      }
      setEditingItem(updatedItem);
      setSuccess('İçerik başarıyla çevrildi.');
    } catch (err) {
      setError('Çeviri sırasında bir hata oluştu.');
    } finally {
      setTranslatingSection(null);
    }
  };

  const saveDialogItem = async () => {
    if (!homePageData || !editingItem) return;
    const keyMap = {
      slide: { section: 'heroSection', list: 'slides' },
      feature: { section: 'featuresSection', list: 'features' },
      stat: { section: 'statsSection', list: 'stats' },
      testimonial: { section: 'testimonialsSection', list: 'testimonials' },
    };
    const { section: sectionKey, list: listKey } = keyMap[dialogType];
    const sectionData = homePageData[sectionKey];
    const newItems = [...(sectionData[listKey] || [])];
    const itemIndex = newItems.findIndex((i: any) => i.id === editingItem.id);
    if (itemIndex > -1) newItems[itemIndex] = editingItem;
    else newItems.push(editingItem);
    updateSection(`${sectionKey}.${listKey}`, newItems);
    closeDialog();
    setSuccess('Öğe kaydedildi. Değişikliklerin kalıcı olması için "Tüm Değişiklikleri Kaydet" butonuna tıklayın.');
  };

  const deleteItem = async (type: 'slide' | 'feature' | 'stat' | 'testimonial', id: string) => {
    if (!homePageData) return;
    const keyMap = {
      slide: { section: 'heroSection', list: 'slides' },
      feature: { section: 'featuresSection', list: 'features' },
      stat: { section: 'statsSection', list: 'stats' },
      testimonial: { section: 'testimonialsSection', list: 'testimonials' },
    };
    const { section: sectionKey, list: listKey } = keyMap[type];
    const newItems = (homePageData[sectionKey]?.[listKey] || []).filter((i: any) => i.id !== id);
    updateSection(`${sectionKey}.${listKey}`, newItems);
    setSuccess('Öğe silindi. Değişikliklerin kalıcı olması için "Tüm Değişiklikleri Kaydet" butonuna tıklayın.');
  };

  const renderMultiLanguageTextFields = (section: string, field: string, label: string, multiline = false, rows = 1) => {
    const value = getTranslatedData(homePageData?.[section]?.[field], currentLang);
    return <TextField fullWidth margin="normal" multiline={multiline} rows={rows} label={`${label} (${languageLabels[currentLang]})`} value={value || ''} onChange={(e) => updateSection(`${section}.${field}.${currentLang}`, e.target.value)} />;
  };

  const renderItemDialog = () => (
    <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
      <DialogTitle>{editingItem?.id ? 'Düzenle' : 'Yeni Ekle'}</DialogTitle>
      <DialogContent>
        <Tabs value={currentLang} onChange={handleLangChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {languages.map(lang => <Tab key={lang} label={languageLabels[lang]} value={lang} />)}
        </Tabs>
        <Box sx={{ pt: 2 }}>
            {dialogType === 'slide' && <>
                <TextField fullWidth margin="normal" label={`Başlık (${languageLabels[currentLang]})`} value={editingItem.title?.[currentLang] || ''} onChange={(e) => handleDialogChange('title', e.target.value, currentLang)} />
                <TextField fullWidth margin="normal" label={`Alt Başlık (${languageLabels[currentLang]})`} value={editingItem.subtitle?.[currentLang] || ''} onChange={(e) => handleDialogChange('subtitle', e.target.value, currentLang)} />
                <TextField fullWidth margin="normal" multiline rows={3} label={`Açıklama (${languageLabels[currentLang]})`} value={editingItem.description?.[currentLang] || ''} onChange={(e) => handleDialogChange('description', e.target.value, currentLang)} />
                <TextField fullWidth margin="normal" label={`Buton Metni (${languageLabels[currentLang]})`} value={editingItem.ctaText?.[currentLang] || ''} onChange={(e) => handleDialogChange('ctaText', e.target.value, currentLang)} />
            </>}
            {dialogType === 'feature' && <>
                <TextField fullWidth margin="normal" label={`Başlık (${languageLabels[currentLang]})`} value={editingItem.title?.[currentLang] || ''} onChange={(e) => handleDialogChange('title', e.target.value, currentLang)} />
                <TextField fullWidth margin="normal" multiline rows={3} label={`Açıklama (${languageLabels[currentLang]})`} value={editingItem.description?.[currentLang] || ''} onChange={(e) => handleDialogChange('description', e.target.value, currentLang)} />
            </>}
             {dialogType === 'stat' && <TextField fullWidth margin="normal" label={`Başlık (${languageLabels[currentLang]})`} value={editingItem.title?.[currentLang] || ''} onChange={(e) => handleDialogChange('title', e.target.value, currentLang)} /> }
            {dialogType === 'testimonial' && <>
                <TextField fullWidth margin="normal" label={`Yazar (${languageLabels[currentLang]})`} value={editingItem.author?.[currentLang] || ''} onChange={(e) => handleDialogChange('author', e.target.value, currentLang)} />
                <TextField fullWidth margin="normal" label={`Konum (${languageLabels[currentLang]})`} value={editingItem.location?.[currentLang] || ''} onChange={(e) => handleDialogChange('location', e.target.value, currentLang)} />
                <TextField fullWidth margin="normal" multiline rows={4} label={`Yorum (${languageLabels[currentLang]})`} value={editingItem.quote?.[currentLang] || ''} onChange={(e) => handleDialogChange('quote', e.target.value, currentLang)} />
            </>}
        </Box>
        <Divider sx={{ my: 2 }} />
        {dialogType === 'slide' && <>
          <TextField fullWidth margin="normal" label="Buton Linki" value={editingItem.ctaLink || ''} onChange={(e) => handleDialogChange('ctaLink', e.target.value)} />
          <ImageUploadField label="Arkaplan Resmi" storagePath="homepage/slides" value={editingItem.backgroundImage || ''} onChange={(url) => handleDialogChange('backgroundImage', url)} />
        </>}
        {dialogType === 'feature' && <TextField fullWidth margin="normal" label="İkon (MUI İkon Adı)" value={editingItem.icon || ''} onChange={(e) => handleDialogChange('icon', e.target.value)} />}
        {dialogType === 'stat' && <>
            <TextField fullWidth margin="normal" label="Değer" value={editingItem.value || ''} onChange={(e) => handleDialogChange('value', e.target.value)} />
            <TextField fullWidth margin="normal" label="İkon (MUI İkon Adı)" value={editingItem.icon || ''} onChange={(e) => handleDialogChange('icon', e.target.value)} />
        </>}
        {dialogType === 'testimonial' && <>
          <ImageUploadField label="Avatar" storagePath="homepage/testimonials" value={editingItem.avatar || ''} onChange={(url) => handleDialogChange('avatar', url)} />
          <Box sx={{ mt: 2 }}><Typography component="legend">Puan</Typography><Rating value={editingItem.rating} onChange={(_e, val) => handleDialogChange('rating', val)} /></Box>
        </>}
        <FormControlLabel control={<Switch checked={editingItem?.isActive} onChange={(e) => handleDialogChange('isActive', e.target.checked)} />} label="Aktif" />
      </DialogContent>
      <DialogActions>
        <Button startIcon={<TranslateIcon />} onClick={handleTranslateDialog} disabled={saving || translatingSection === 'dialog'}>{translatingSection === 'dialog' ? 'Çevriliyor...' : 'Tümünü Çevir'}</Button>
        <Button onClick={closeDialog} color="secondary">İptal</Button>
        <Button onClick={saveDialogItem} color="primary" variant="contained" disabled={saving}>Kaydet</Button>
      </DialogActions>
    </Dialog>
  );

  const renderSectionHeader = (title: string, sectionKey: string) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <FormControlLabel control={<Switch checked={homePageData?.[sectionKey]?.isActive || false} onChange={(e) => updateSection(`${sectionKey}.isActive`, e.target.checked)} />} label="Bölüm Aktif" />
    </Box>
  );

  const renderListSection = (title: string, sectionKey: string, listKey: string, dialogType: 'slide' | 'feature' | 'stat' | 'testimonial', primaryTextField: string, secondaryTextField?: string) => (
    <Card>
      <CardContent>
        {renderSectionHeader(title, sectionKey)}
        <Divider sx={{ my: 2 }} />
        <Button startIcon={<AddIcon />} onClick={() => openDialog(dialogType)} variant="outlined">Yeni Ekle</Button>
        <List>
          {homePageData?.[sectionKey]?.[listKey]?.map((item: any) => (
            <ListItem key={item.id} secondaryAction={<>
                <Tooltip title="Websitesinde göster/gizle">
                    <Switch edge="end" checked={item.isActive} onChange={(e) => {
                        const updatedItems = homePageData[sectionKey][listKey].map((i: any) => i.id === item.id ? { ...i, isActive: e.target.checked } : i);
                        updateSection(`${sectionKey}.${listKey}`, updatedItems);
                    }}/>
                </Tooltip>
                <IconButton edge="end" onClick={() => openDialog(dialogType, item)}><EditIcon /></IconButton>
                <IconButton edge="end" onClick={() => deleteItem(dialogType, item.id)}><DeleteIcon /></IconButton>
            </>}>
              {dialogType === 'testimonial' && <Avatar src={item.avatar} sx={{ mr: 2 }} />}
              <ListItemText primary={getTranslatedData(item[primaryTextField], 'tr') || 'Başlıksız'} secondary={secondaryTextField ? (getTranslatedData(item[secondaryTextField], 'tr') || item[secondaryTextField]) : ''} />
              {dialogType === 'testimonial' && <Rating value={item.rating} readOnly />}
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>;
  if (!homePageData) return <Alert severity="error">Ana sayfa verileri yüklenemedi.</Alert>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 4 }}>
        <Typography variant="h4">Ana Sayfa Yönetimi</Typography>
        <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={<SaveIcon />}>{saving ? 'Kaydediliyor...' : 'Tüm Değişiklikleri Kaydet'}</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="homepage sections tabs">
          <Tab icon={<HomeIcon />} label="Hero Bölümü" />
          <Tab icon={<StarIcon />} label="Özellikler Bölümü" />
          <Tab icon={<TrendingUpIcon />} label="İstatistikler Bölümü" />
          <Tab icon={<ImageIcon />} label="Hakkımızda Bölümü" />
          <Tab icon={<TrendingUpIcon />} label="Popüler Turlar Bölümü" />
          <Tab icon={<StarIcon />} label="Yorumlar Bölümü" />
          <Tab icon={<ArticleIcon />} label="Blog Bölümü" />
          <Tab icon={<CtaIcon />} label="Özel Tur Talebi Bölümü" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>{renderListSection("Hero Bölümü", "heroSection", "slides", "slide", "title", "subtitle")}</TabPanel>
      <TabPanel value={tabValue} index={1}>{renderListSection("Özellikler Bölümü", "featuresSection", "features", "feature", "title", "description")}</TabPanel>
      <TabPanel value={tabValue} index={2}>{renderListSection("İstatistikler Bölümü", "statsSection", "stats", "stat", "title", "value")}</TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <Card><CardContent>
            {renderSectionHeader("Hakkımızda Bölümü", "aboutUsSection")}
            <Divider sx={{my:2}} />
            <Tabs value={currentLang} onChange={handleLangChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>{languages.map(lang => <Tab key={lang} label={languageLabels[lang]} value={lang} />)}</Tabs>
            <Box>
                {renderMultiLanguageTextFields('aboutUsSection', 'title', 'Başlık')}
                {renderMultiLanguageTextFields('aboutUsSection', 'subtitle', 'Alt Başlık')}
                {renderMultiLanguageTextFields('aboutUsSection', 'description', 'Açıklama', true, 5)}
                {renderMultiLanguageTextFields('aboutUsSection', 'ctaText', 'Buton Metni')}
            </Box>
            <TextField fullWidth margin="normal" label="Buton Linki" value={homePageData?.aboutUsSection?.ctaLink || ''} onChange={(e) => updateSection('aboutUsSection.ctaLink', e.target.value)} />
            <ImageUploadField label="Hakkımızda Resmi" storagePath="homepage/about" value={homePageData?.aboutUsSection?.image || ''} onChange={(url) => updateSection('aboutUsSection.image', url)} />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button startIcon={<TranslateIcon />} onClick={() => handleTranslateSection('aboutUsSection', ['title', 'subtitle', 'description', 'ctaText'])} disabled={saving || !!translatingSection}>
                    {translatingSection === 'aboutUsSection' ? 'Çevriliyor...' : 'Bu Bölümü Çevir'}
                </Button>
            </Box>
        </CardContent></Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card><CardContent>
            {renderSectionHeader("Popüler Turlar Bölümü", "popularToursSection")}
            <Divider sx={{my:2}} />
            <Tabs value={currentLang} onChange={handleLangChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>{languages.map(lang => <Tab key={lang} label={languageLabels[lang]} value={lang} />)}</Tabs>
            <Box>
                {renderMultiLanguageTextFields('popularToursSection', 'title', 'Başlık')}
                {renderMultiLanguageTextFields('popularToursSection', 'subtitle', 'Alt Başlık', true, 2)}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button startIcon={<TranslateIcon />} onClick={() => handleTranslateSection('popularToursSection', ['title', 'subtitle'])} disabled={saving || !!translatingSection}>
                {translatingSection === 'popularToursSection' ? 'Çevriliyor...' : 'Bu Bölümü Çevir'}
              </Button>
            </Box>
        </CardContent></Card>
      </TabPanel>
      
      <TabPanel value={tabValue} index={5}>{renderListSection("Yorumlar Bölümü", "testimonialsSection", "testimonials", "testimonial", "author", "quote")}</TabPanel>

      <TabPanel value={tabValue} index={6}>
        <Card><CardContent>
            {renderSectionHeader("Blog Bölümü", "blogSection")}
            <Divider sx={{my:2}} />
            <Tabs value={currentLang} onChange={handleLangChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>{languages.map(lang => <Tab key={lang} label={languageLabels[lang]} value={lang} />)}</Tabs>
            <Box>
                {renderMultiLanguageTextFields('blogSection', 'title', 'Başlık')}
                {renderMultiLanguageTextFields('blogSection', 'subtitle', 'Alt Başlık', true, 2)}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button startIcon={<TranslateIcon />} onClick={() => handleTranslateSection('blogSection', ['title', 'subtitle'])} disabled={saving || !!translatingSection}>
                {translatingSection === 'blogSection' ? 'Çevriliyor...' : 'Bu Bölümü Çevir'}
              </Button>
            </Box>
        </CardContent></Card>
      </TabPanel>
      
      <TabPanel value={tabValue} index={7}>
        <Card><CardContent>
            {renderSectionHeader("Özel Tur Talebi Bölümü", "ctaSection")}
            <Divider sx={{my:2}} />
            <Tabs value={currentLang} onChange={handleLangChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>{languages.map(lang => <Tab key={lang} label={languageLabels[lang]} value={lang} />)}</Tabs>
            <Box>
                {renderMultiLanguageTextFields('ctaSection', 'title', 'Başlık')}
                {renderMultiLanguageTextFields('ctaSection', 'subtitle', 'Alt Başlık', true, 2)}
                {renderMultiLanguageTextFields('ctaSection', 'primaryCtaText', 'Ana Buton Metni')}
                <TextField fullWidth margin="normal" label="Ana Buton Linki" value={homePageData?.ctaSection?.primaryCtaLink || ''} onChange={(e) => updateSection('ctaSection.primaryCtaLink', e.target.value)} />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button startIcon={<TranslateIcon />} onClick={() => handleTranslateSection('ctaSection', ['title', 'subtitle'])} disabled={saving || !!translatingSection}>
                {translatingSection === 'ctaSection' ? 'Çevriliyor...' : 'Bu Bölümü Çevir'}
              </Button>
            </Box>
        </CardContent></Card>
      </TabPanel>

      {dialogOpen && renderItemDialog()}
    </Container>
  );
};

export default ManageHomePagePage; 